import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import {
    useNavigate,
    useParams,
} from 'react-router-dom';
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    InputGroup,
    Row,
    Spinner,
} from 'react-bootstrap';
import { Formik } from 'formik';

import API from '../../../api/axios';
import { useFormat } from '../../../components/FormatContext';

const DISCOUNT_TYPE_OPTIONS = [
    {
        value: 'PERCENTAGE',
        label: 'Percentage',
    },
    {
        value: 'FIXED_AMOUNT',
        label: 'Fixed Amount',
    },
];

const BASE_INITIAL_VALUES = {
    code: '',
    description: '',
    type: 'PERCENTAGE',
    discount: '10',

    validStartDate: '',
    validEndDate: '',

    rentStartDate: '',
    rentEndDate: '',

    userLimit: 100,
    used: 0,

    createdAt: null,
    updatedAt: null,

    isExhausted: false,
    isValidNow: false,
};

const PromoCodeFormPage = () => {
    const {
        baseFormatters,
    } = useFormat();

    const { id } =
        useParams();

    const navigate =
        useNavigate();

    const isEdit =
        Boolean(id);

    const [loading, setLoading] =
        useState(isEdit);

    const [saving, setSaving] =
        useState(false);

    const [error, setError] =
        useState('');

    const [
        initialValues,
        setInitialValues,
    ] = useState(
        () => ({
            ...BASE_INITIAL_VALUES,
        }),
    );

    const loadPromoCode =
        useCallback(async () => {
            if (!isEdit) {
                return null;
            }

            const { data } =
                await API.get(
                    `/codes/promo/${id}`,
                );

            return data;
        }, [
            id,
            isEdit,
        ]);

    useEffect(() => {
        if (!isEdit) {
            return undefined;
        }

        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);
                setError('');

                const data =
                    await loadPromoCode();

                if (!mounted) {
                    return;
                }

                setInitialValues({
                    code:
                        data?.code ||
                        '',

                    description:
                        data?.description ||
                        '',

                    type:
                        data?.type ||
                        'PERCENTAGE',

                    discount:
                        data?.discount ??
                        '10',

                    validStartDate:
                        data?.validStartDate?.slice(
                            0,
                            16,
                        ) || '',

                    validEndDate:
                        data?.validEndDate?.slice(
                            0,
                            16,
                        ) || '',

                    rentStartDate:
                        data?.rentStartDate?.slice(
                            0,
                            16,
                        ) || '',

                    rentEndDate:
                        data?.rentEndDate?.slice(
                            0,
                            16,
                        ) || '',

                    userLimit:
                        data?.userLimit ??
                        100,

                    used:
                        data?.used ??
                        0,

                    createdAt:
                        data?.createdAt ??
                        null,

                    updatedAt:
                        data?.updatedAt ??
                        null,

                    isExhausted:
                        !!data?.isExhausted,

                    isValidNow:
                        !!data?.isValidNow,
                });
            } catch (e) {
                if (mounted) {
                    setError(
                        e?.response?.data
                            ?.message ||
                        e?.message ||
                        'Failed to load',
                    );
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [
        isEdit,
        loadPromoCode,
    ]);

    const handleSubmit =
        useCallback(
            async (values) => {
                try {
                    setSaving(true);
                    setError('');

                    const payload = {
                        code:
                            values.code
                                .trim()
                                .toUpperCase(),

                        description:
                            values.description
                                ?.trim() ||
                            null,

                        type:
                            values.type,

                        discount:
                            values.discount,

                        validStartDate:
                            values.validStartDate,

                        validEndDate:
                            values.validEndDate,

                        rentStartDate:
                            values.rentStartDate,

                        rentEndDate:
                            values.rentEndDate,

                        userLimit:
                            Number(
                                values.userLimit,
                            ),
                    };

                    if (isEdit) {
                        await API.patch(
                            `/codes/promo/${id}`,
                            payload,
                        );

                        alert(
                            'Updated successfully',
                        );

                        return;
                    }

                    const {
                        data,
                    } =
                        await API.post(
                            '/codes/promo',
                            payload,
                        );

                    alert(
                        'Created successfully',
                    );

                    navigate(
                        `/codes/promo/${data.id}`,
                    );
                } catch (e) {
                    setError(
                        e?.response?.data
                            ?.message ||
                        e?.message ||
                        'Save failed',
                    );
                } finally {
                    setSaving(false);
                }
            },
            [
                id,
                isEdit,
                navigate,
            ],
        );

    return (
        <Formik
            initialValues={
                initialValues
            }
            enableReinitialize
            onSubmit={
                handleSubmit
            }
        >
            {({
                handleSubmit:
                formikHandleSubmit,
                values,
                setFieldValue,
            }) => (
                <Container fluid>
                    <Row className="mb-4">
                        <Col sm={8}>
                            <span className="fw-semibold">
                                {isEdit
                                    ? 'Offer Code Details'
                                    : 'Create Offer Code'}
                            </span>
                        </Col>
                    </Row>

                    <Card className="p-4">
                        {loading ? (
                            <Spinner animation="border" />
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Code *
                                    </Form.Label>

                                    <Form.Control
                                        value={
                                            values.code
                                        }
                                        onChange={(
                                            e,
                                        ) =>
                                            setFieldValue(
                                                'code',
                                                e
                                                    .target
                                                    .value,
                                            )
                                        }
                                        disabled={
                                            isEdit
                                        }
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Description
                                    </Form.Label>

                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={
                                            values.description
                                        }
                                        onChange={(
                                            e,
                                        ) =>
                                            setFieldValue(
                                                'description',
                                                e
                                                    .target
                                                    .value,
                                            )
                                        }
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Type
                                    </Form.Label>

                                    <Form.Select
                                        value={
                                            values.type
                                        }
                                        onChange={(
                                            e,
                                        ) =>
                                            setFieldValue(
                                                'type',
                                                e
                                                    .target
                                                    .value,
                                            )
                                        }
                                    >
                                        {DISCOUNT_TYPE_OPTIONS.map(
                                            (
                                                option,
                                            ) => (
                                                <option
                                                    key={
                                                        option.value
                                                    }
                                                    value={
                                                        option.value
                                                    }
                                                >
                                                    {
                                                        option.label
                                                    }
                                                </option>
                                            ),
                                        )}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Discount
                                    </Form.Label>

                                    <InputGroup>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={
                                                values.discount
                                            }
                                            onChange={(
                                                e,
                                            ) =>
                                                setFieldValue(
                                                    'discount',
                                                    e
                                                        .target
                                                        .value,
                                                )
                                            }
                                        />

                                        <InputGroup.Text>
                                            {values.type ===
                                                'PERCENTAGE'
                                                ? '%'
                                                : ''}
                                        </InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>

                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Label>
                                            Valid
                                            Start
                                        </Form.Label>

                                        <Form.Control
                                            type="datetime-local"
                                            value={
                                                values.validStartDate
                                            }
                                            onChange={(
                                                e,
                                            ) =>
                                                setFieldValue(
                                                    'validStartDate',
                                                    e
                                                        .target
                                                        .value,
                                                )
                                            }
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Form.Label>
                                            Valid
                                            End
                                        </Form.Label>

                                        <Form.Control
                                            type="datetime-local"
                                            value={
                                                values.validEndDate
                                            }
                                            onChange={(
                                                e,
                                            ) =>
                                                setFieldValue(
                                                    'validEndDate',
                                                    e
                                                        .target
                                                        .value,
                                                )
                                            }
                                        />
                                    </Col>
                                </Row>

                                <Row className="g-3 mt-2">
                                    <Col md={6}>
                                        <Form.Label>
                                            Rent
                                            Start
                                        </Form.Label>

                                        <Form.Control
                                            type="datetime-local"
                                            value={
                                                values.rentStartDate
                                            }
                                            onChange={(
                                                e,
                                            ) =>
                                                setFieldValue(
                                                    'rentStartDate',
                                                    e
                                                        .target
                                                        .value,
                                                )
                                            }
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Form.Label>
                                            Rent
                                            End
                                        </Form.Label>

                                        <Form.Control
                                            type="datetime-local"
                                            value={
                                                values.rentEndDate
                                            }
                                            onChange={(
                                                e,
                                            ) =>
                                                setFieldValue(
                                                    'rentEndDate',
                                                    e
                                                        .target
                                                        .value,
                                                )
                                            }
                                        />
                                    </Col>
                                </Row>

                                <Form.Group className="mt-3">
                                    <Form.Label>
                                        User
                                        Limit
                                    </Form.Label>

                                    <Form.Control
                                        type="number"
                                        value={
                                            values.userLimit
                                        }
                                        onChange={(
                                            e,
                                        ) =>
                                            setFieldValue(
                                                'userLimit',
                                                e
                                                    .target
                                                    .value,
                                            )
                                        }
                                    />
                                </Form.Group>

                                {isEdit && (
                                    <Card className="p-3 mt-4">
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <div>
                                                    Used:{' '}
                                                    {
                                                        values.used
                                                    }
                                                </div>

                                                <div>
                                                    Status:{' '}

                                                    {values.isExhausted ? (
                                                        <Badge bg="danger">
                                                            Exhausted
                                                        </Badge>
                                                    ) : values.isValidNow ? (
                                                        <Badge bg="success">
                                                            Valid
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="secondary">
                                                            Inactive
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <div>
                                                    Created:{' '}

                                                    {values.createdAt
                                                        ? baseFormatters.date.numeric.format(
                                                            new Date(
                                                                values.createdAt,
                                                            ),
                                                        )
                                                        : '—'}
                                                </div>

                                                <div>
                                                    Updated:{' '}

                                                    {values.updatedAt
                                                        ? baseFormatters.date.numeric.format(
                                                            new Date(
                                                                values.updatedAt,
                                                            ),
                                                        )
                                                        : '—'}
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                )}

                                {error && (
                                    <div className="text-danger mt-3">
                                        {
                                            error
                                        }
                                    </div>
                                )}

                                <div className="mt-4 text-end">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() =>
                                            navigate(
                                                '/codes/promo',
                                            )
                                        }
                                    >
                                        Back
                                    </Button>{' '}

                                    <Button
                                        type="button"
                                        onClick={() =>
                                            formikHandleSubmit()
                                        }
                                        disabled={
                                            saving
                                        }
                                    >
                                        {saving
                                            ? 'Saving…'
                                            : 'Save'}
                                    </Button>
                                </div>
                            </>
                        )}
                    </Card>
                </Container>
            )}
        </Formik>
    );
};

export default PromoCodeFormPage;